/**
 * paginateWords - Pure auto-pagination algorithm
 * 
 * Given an array of pages (each with words), splits them into virtual pages
 * that fit within a given height, using a measurement function callback.
 * 
 * Extracted from BookReadingView.tsx useAutoPagination hook for testability.
 * The hook still handles React state + DOM measurement; this module contains
 * the algorithm only.
 */

export interface PaginationWord {
  word: string;
  turnCode: string | null;
  inLexicon: boolean;
}

export interface PaginationPage {
  text: string;
  turnArtUrl: string | null;
  words: PaginationWord[];
}

/**
 * Callback to measure the rendered height of a string of text.
 * In production this is backed by a hidden DOM element.
 * In tests this can be a simple word-count-based estimator.
 */
export type MeasureTextHeight = (text: string) => number;

/**
 * Split pages into virtual pages that fit within `availableHeight`.
 * 
 * Algorithm:
 * - For each logical page, measure if the full text fits
 * - If not, binary-search for the maximum number of words that fit
 * - Repeat until all words are placed
 * 
 * Guarantees at least 1 word per virtual page (prevents infinite loops).
 */
export function paginateWords(
  pages: PaginationPage[],
  availableHeight: number,
  measureHeight: MeasureTextHeight,
): PaginationPage[] {
  if (pages.length === 0 || availableHeight <= 0) {
    return pages;
  }

  const result: PaginationPage[] = [];

  for (const page of pages) {
    const words = page.words;
    if (words.length === 0) {
      result.push(page);
      continue;
    }

    // Measure the full page text height
    const fullText = words.map(w => w.word).join(' ');
    const fullHeight = measureHeight(fullText);

    // If it fits, no splitting needed
    if (fullHeight <= availableHeight) {
      result.push(page);
      continue;
    }

    // Binary-search for how many words fit per virtual page
    let startIdx = 0;
    while (startIdx < words.length) {
      let lo = 1;
      let hi = words.length - startIdx;
      let bestFit = 1; // At minimum one word per page

      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        const slice = words.slice(startIdx, startIdx + mid);
        const text = slice.map(w => w.word).join(' ');

        if (measureHeight(text) <= availableHeight) {
          bestFit = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      const pageWords = words.slice(startIdx, startIdx + bestFit);
      result.push({
        text: pageWords.map(w => w.word).join(' '),
        turnArtUrl: null,
        words: pageWords,
      });
      startIdx += bestFit;
    }
  }

  return result;
}
